import { uniq } from 'lodash';
import type { ComparatorReturns, GroupingDirective, SortingDirective } from '@list-store';
import {
    AttributeTypeEnum,
    UnitQueueWireDto,
    ColumnDefinitionEnum,
    QueueColumnFilter,
} from '@mark43/cad-api';
import type { ColumnDefinitionEnumType } from '@mark43/cad-api';
import { action, makeObservable, override } from 'mobx';
import { alphabeticalStringComparator } from '../../common/sorting/comparators';
import { EventNotificationsStore } from '../../event-notifications/event-notifications-store';
import { WorkspaceQueue } from '../../panels/workspace-queue';
import { WorkspacePanelWithQueue } from '../../panels/workspace-queue-exists';
import { QueueStore } from '../../queues/store/queue-store';
import type { UnitQueueRowData } from '../../socket/stream-types';
import { UnitTimer, UnitTimersStore } from '../../unit-timers/unit-timers-store';
import type { WithColumnDefinition } from '../../event-queues/get-event-filter-options';
import type { Option } from '../../forms/option';
import { logWarn } from '../../monitoring/log';
import { isPrimaryUnit } from '../utils';
import { UNIT_EMERGENCY_LABEL } from '../constants';

const callSignSortingDirective: SortingDirective<UnitQueueRowData> = {
    getSortValue: (unitQueueView) => unitQueueView.callSign!,
    comparator: (left: string, right: string) =>
        alphabeticalStringComparator(left, right) as ComparatorReturns,
};

export class UnitQueueStore extends QueueStore<UnitQueueRowData> {
    private unitTimersStore: UnitTimersStore;

    /**
     * trackedEvents and trackedUnitEvents are used to aid in the generation of Event Notifications from the Unit Queue.
     * These two maps allow us to make a determination like "I care about this event related nudge" in O(1) time.
     */
    public trackedEvents: { [eventId: number]: { [unitId: number]: true } } = {};
    public trackedUnitEvents: { [unitId: number]: number } = {};

    constructor({
        workspacePanel,
        eventNotificationsStore,
        unitTimersStore,
        manualFilterDirective,
        sortingDirective,
        groupingDirective,
        defaultSortingDirective,
    }: {
        workspacePanel: WorkspacePanelWithQueue;
        eventNotificationsStore: EventNotificationsStore;
        unitTimersStore: UnitTimersStore;
        manualFilterDirective?: boolean;
        sortingDirective?: SortingDirective<UnitQueueRowData>;
        groupingDirective?: GroupingDirective<UnitQueueRowData>;
        defaultSortingDirective?: SortingDirective<UnitQueueRowData>;
    }) {
        super({
            workspacePanel,
            eventNotificationsStore,
            playAedSoundForEvent: () => {},
            defaultSortingDirective: defaultSortingDirective ?? callSignSortingDirective,
            manualFilterDirective,
            sortingDirective,
            groupingDirective,
        });
        this.unitTimersStore = unitTimersStore;

        if (this.workspacePanel.queue.pinBackupsToPrimaryUnits) {
            this.setPinToPrimarySortingAndGroupingDirectives();
        } else {
            this.setCombinedSortingAndGroupingDirectives();
        }
        makeObservable(this);
    }

    @action
    setUnitTypeAttrIds(unitTypeAttrIds: number[]): void {
        this.workspacePanel.queue.unitTypeFilters = unitTypeAttrIds.map((id: number) => ({
            attributeId: id,
            attributeType: AttributeTypeEnum.UNIT_TYPE.name,
            queueId: this.workspacePanel.queue.id,
        }));
    }

    @action
    setUnitQueueColumnFilters(
        selectedUnitQueueFilterOptions: WithColumnDefinition<Option>[]
    ): void {
        // preserve other existing filters
        const preservedACfsDispatcherFilters = (
            this.workspacePanel.queue.unitQueueColumnFilters ?? []
        ).filter((filter) => filter.columnDefinition === ColumnDefinitionEnum.ADDITIONAL_CFS.name);
        const preservedEventPrimarySubdivFilters = (
            this.workspacePanel.queue.unitQueueColumnFilters ?? []
        ).filter((filter) => filter.columnDefinition === ColumnDefinitionEnum.BEAT.name);
        const preservedUnitDispatchAreasFilters = (
            this.workspacePanel.queue.unitQueueColumnFilters ?? []
        ).filter(
            (filter) => filter.columnDefinition === ColumnDefinitionEnum.UNIT_DISPATCH_AREA.name
        );
        const preservedEventDispatchAreasFilters = (
            this.workspacePanel.queue.unitQueueColumnFilters ?? []
        ).filter((filter) => filter.columnDefinition === ColumnDefinitionEnum.DISPATCH_AREA.name);

        const newFilters = selectedUnitQueueFilterOptions.map(
            (option): QueueColumnFilter => ({
                entityId: option.value,
                columnDefinition: option.columnDefinition,
                queueId: this.workspacePanel.queue.id,
            })
        );

        this.workspacePanel.queue.unitQueueColumnFilters = [
            ...preservedACfsDispatcherFilters,
            ...preservedEventPrimarySubdivFilters,
            ...preservedUnitDispatchAreasFilters,
            ...preservedEventDispatchAreasFilters,
            ...newFilters,
        ];
    }

    @action
    upsertUnitQueueFilters(
        unitQueueFilterOptionsToAdd: WithColumnDefinition<Option>[],
        columnDefinition: ColumnDefinitionEnumType
    ): void {
        const newFilters = unitQueueFilterOptionsToAdd.map(
            (option): QueueColumnFilter => ({
                entityId: option.value,
                columnDefinition: option.columnDefinition,
                queueId: this.workspacePanel.queue.id,
            })
        );
        const filteredExistingFilters = (
            this.workspacePanel.queue.unitQueueColumnFilters ?? []
        ).filter((filter) => filter.columnDefinition !== columnDefinition);

        this.workspacePanel.queue.unitQueueColumnFilters = [
            ...filteredExistingFilters,
            ...newFilters,
        ];
    }

    @override
    public setRows(rows: UnitQueueRowData[]): void {
        this.trackedEvents = {};
        this.trackedUnitEvents = {};
        for (const row of rows) {
            if (row.eventQueueView) {
                this.trackedUnitEvents[row.id] = row.eventQueueView.id;
                if (!this.trackedEvents[row.eventQueueView.id]) {
                    this.trackedEvents[row.eventQueueView.id] = {};
                }
                this.trackedEvents[row.eventQueueView.id][row.id] = true;
            }
            this.unitTimersStore.refreshTimer(row, false);
        }
        super.setRows(rows);
    }

    private getOtherUnitQueueViewsAssignedToSameEvents(unitIds: number[]) {
        const units = uniq(unitIds)
            .map((unitId) => {
                return this.listStore.getItemById(unitId);
            })
            .filter((item) => !!item) as UnitQueueRowData[];
        return uniq(
            units.reduce((acc, unit) => {
                const eventId = unit.eventQueueView?.id;
                let otherUnitIdsAssignedToSameEvent: number[] = [];
                if (eventId) {
                    otherUnitIdsAssignedToSameEvent = Object.keys(
                        this.trackedEvents[eventId]
                    ).map((val) => parseInt(val));
                }
                return [...acc, ...otherUnitIdsAssignedToSameEvent];
            }, [] as number[])
        )
            .map((id) => {
                return this.listStore.getItemById(id);
            })
            .filter((unitQueueView): unitQueueView is UnitQueueRowData => !!unitQueueView);
    }

    @override
    public upsertRows(
        rows: UnitQueueRowData[],
        shouldUpsertOtherUnitQueueViewsAssignedToSameEvents = false
    ): void {
        let rowsToUpsert = rows;
        if (shouldUpsertOtherUnitQueueViewsAssignedToSameEvents) {
            const rowsIdsToUpsert = rows.map((row) => row.id);
            // get other UnitQueueViews that are assigned to the same events, but not in the upcoming 'rows' for upsert
            const otherUnitQueueViewsToUpsert = this.getOtherUnitQueueViewsAssignedToSameEvents(
                rows.map((uqv) => uqv.id)
            ).filter((row) => !rowsIdsToUpsert.includes(row.id));
            rowsToUpsert = otherUnitQueueViewsToUpsert.concat(rows);
        }
        for (const row of rowsToUpsert) {
            const eventId = row.eventQueueView?.id;
            const storedRowState = this.listStore.getItemById(row.id);

            if (eventId) {
                if (!this.trackedEvents[eventId]) {
                    this.trackedEvents[eventId] = {};
                }
                this.trackedEvents[eventId][row.id] = true;
                this.trackedUnitEvents[row.id] = eventId;
            } else {
                const trackedUnitEventId = this.trackedUnitEvents[row.id];
                if (trackedUnitEventId) {
                    delete this.trackedUnitEvents[row.id];
                    delete this.trackedEvents[trackedUnitEventId][row.id];
                }
            }

            this.unitTimersStore.refreshTimer(
                row,
                storedRowState?.hasInProgressEmergency === row.hasInProgressEmergency
            );
        }
        // in the case of pin to primary it's important that "primary units" are upserted first
        // because our algorithm to place a backup unit in the correct position in the list
        // relies on the position of its primary
        const sortedUnitQueueViews = rows.slice().sort((a, b) => {
            if (a.hasBackups && !b.hasBackups) {
                return -1;
            }
            if (!a.hasBackups && b?.hasBackups) {
                return 1;
            }
            return 0;
        });
        super.upsertRows(sortedUnitQueueViews);
        /** Force a full re-sort so that backup units not included in this upsert
         * (e.g. not tracked in trackedEvents) are also repositioned using fresh
         * primary data via resolveLivePrimary in the sort comparator. The directives
         * are already in place, so recompute once rather than re-setting the sorting
         * and grouping directives (which would rebuild the whole list twice) */
        if (shouldUpsertOtherUnitQueueViewsAssignedToSameEvents) {
            this.listStore.recompute();
        }
    }

    @override
    public removeRows(ids: number[]): void {
        for (const id of ids) {
            const trackedEventId = this.trackedUnitEvents[id];
            if (trackedEventId) {
                const trackedEvent = this.trackedEvents[trackedEventId];
                if (trackedEvent) {
                    delete this.trackedEvents[trackedEventId][id];
                } else {
                    logWarn('Unexpectedly did not find entry for event in this.trackedEvents', {
                        unitId: id,
                        eventId: trackedEventId,
                        unit: this.listStore.getItemById(id),
                        event: this.listStore.getItemById(trackedEventId),
                    });
                }
                delete this.trackedUnitEvents[id];
            }
        }
        // in the case of pin to primary it's important that "primary units" are removed last
        // because our algorithm to locate a backup unit to remove it relies on the position of its primary
        const idsToRemove = ids.sort((a, b) => {
            const itemA = this.listStore.getItemById(a);
            const itemB = this.listStore.getItemById(b);
            if (itemA?.hasBackups && !itemB?.hasBackups) {
                return 1;
            }
            if (!itemA?.hasBackups && itemB?.hasBackups) {
                return -1;
            }
            return 0;
        });
        super.removeRows(idsToRemove);
    }

    public getUnitTimer(unitId: number): UnitTimer | undefined {
        return this.unitTimersStore.getUnitTimer(unitId);
    }

    protected createEmergencySortingDirective(
        sortingDirective: SortingDirective<UnitQueueRowData>
    ): SortingDirective<UnitQueueRowData> {
        return {
            getSortValue: (item: UnitQueueRowData) => item,
            comparator: function comparator(left: UnitQueueRowData, right: UnitQueueRowData) {
                if (left.hasInProgressEmergency && !right.hasInProgressEmergency) {
                    return -1;
                } else if (!left.hasInProgressEmergency && right.hasInProgressEmergency) {
                    return 1;
                }
                return sortingDirective.comparator(
                    sortingDirective.getSortValue(left),
                    sortingDirective.getSortValue(right)
                );
            },
        };
    }

    protected createEmergencyGroupingDirective(
        groupingDirective: GroupingDirective<UnitQueueRowData>
    ): GroupingDirective<UnitQueueRowData> {
        return {
            getGroupValue: (item: UnitQueueRowData) => {
                if (item.hasInProgressEmergency) {
                    return UNIT_EMERGENCY_LABEL;
                }
                return groupingDirective.getGroupValue(item);
            },
            comparator: (left: string, right: string) => {
                if (left === UNIT_EMERGENCY_LABEL && right !== UNIT_EMERGENCY_LABEL) {
                    return -1;
                } else if (left !== UNIT_EMERGENCY_LABEL && right === UNIT_EMERGENCY_LABEL) {
                    return 1;
                }
                return groupingDirective.comparator(left, right);
            },
        };
    }

    private createCombinedSortingDirective() {
        const sortingDirective: SortingDirective<UnitQueueRowData> = this.workspacePanel.queue
            .sortByQueueColumnDefinition
            ? this.createSortingDirective(
                  this.workspacePanel.queue.sortByQueueColumnDefinition,
                  this.workspacePanel.queue.sortDirection
              )
            : callSignSortingDirective;
        return this.createEmergencySortingDirective(sortingDirective);
    }

    private createCombinedGroupingDirective() {
        if (!this.workspacePanel.queue.groupByQueueColumnDefinition) {
            return;
        }

        const groupingDirective: GroupingDirective<UnitQueueRowData> = this.createGroupingDirective(
            this.workspacePanel.queue.groupByQueueColumnDefinition,
            this.workspacePanel.queue.groupBySortDirection
        );

        return this.createEmergencyGroupingDirective(groupingDirective);
    }

    private createPinToPrimarySortingDirective(): SortingDirective<UnitQueueRowData> {
        function isPrimary(item: UnitQueueRowData): boolean {
            return isPrimaryUnit(item);
        }

        function isBackup(
            item: UnitQueueRowData
        ): item is Omit<UnitQueueRowData, 'backingUpUnitQueueView'> & {
            backingUpUnitQueueView: UnitQueueWireDto;
        } {
            return !!item.backingUpUnitQueueView;
        }

        function isPrimaryOrBackup(item: UnitQueueRowData): boolean {
            return isBackup(item) || isPrimary(item);
        }

        function isUnassigned(item: UnitQueueRowData): boolean {
            return !isPrimaryOrBackup(item);
        }

        function callSignComparator(
            left: UnitQueueRowData,
            right: UnitQueueRowData
        ): ComparatorReturns {
            return alphabeticalStringComparator(
                left.callSign!,
                right.callSign!
            ) as ComparatorReturns;
        }

        const baseSortingDirective = this.createCombinedSortingDirective();

        const listStore = this.listStore;
        /** Look the primary up live by id (its embedded snapshot goes stale when the
         * primary changes). Uses `peekItemById`, not `getItemById`, because
         * `getItemById` returns a `toJS` deep-clone: this runs for every comparison in
         * the sort and only reads the result, so cloning each call would dominate cost  */
        function resolveLivePrimary(backingUpUnitQueueView: UnitQueueWireDto): UnitQueueRowData {
            return (listStore.peekItemById(backingUpUnitQueueView.id) ??
                backingUpUnitQueueView) as UnitQueueRowData;
        }

        const sortingDirective: SortingDirective<UnitQueueRowData> = {
            getSortValue(item: UnitQueueRowData): UnitQueueRowData {
                return baseSortingDirective.getSortValue(item);
            },
            comparator(left: UnitQueueRowData, right: UnitQueueRowData): ComparatorReturns {
                /**
                 * ListStore uses an item's id as the final sort criteria.
                 * Pin to Primary implements its own sorting, with a different sort value shape,
                 *  so we have to  reimplement using id as the final sort criteria.
                 */
                function enhanceComparator(
                    comparatorFn: (
                        left: UnitQueueRowData,
                        right: UnitQueueRowData
                    ) => ComparatorReturns
                ): (left: UnitQueueRowData, right: UnitQueueRowData) => ComparatorReturns {
                    return (left: UnitQueueRowData, right: UnitQueueRowData): ComparatorReturns => {
                        const baseComparatorReturns = comparatorFn(
                            baseSortingDirective.getSortValue(left),
                            baseSortingDirective.getSortValue(right)
                        );
                        if (baseComparatorReturns === 0) {
                            return callSignComparator(left, right);
                        }
                        return baseComparatorReturns;
                    };
                }
                const enhancedComparator = enhanceComparator(baseSortingDirective.comparator);

                if (!isBackup(left) && !isBackup(right)) {
                    return enhancedComparator(left, right);
                }

                if (isBackup(left) && isUnassigned(right)) {
                    return enhancedComparator(
                        resolveLivePrimary(left.backingUpUnitQueueView),
                        right
                    );
                }
                if (isUnassigned(left) && isBackup(right)) {
                    return enhancedComparator(
                        left,
                        resolveLivePrimary(right.backingUpUnitQueueView)
                    );
                }

                if (isBackup(left) && isBackup(right)) {
                    // if backing up the same unit, compare to each other
                    if (left.backingUpUnitQueueView?.id === right.backingUpUnitQueueView?.id) {
                        return enhancedComparator(left, right);
                    } else {
                        // if backing up different primaries, compare their primaries
                        return enhancedComparator(
                            resolveLivePrimary(left.backingUpUnitQueueView),
                            resolveLivePrimary(right.backingUpUnitQueueView)
                        );
                    }
                }

                if (isBackup(left) && isPrimary(right)) {
                    // if the primary is the primary for this backup, the primary wins
                    if (left.backingUpUnitQueueView?.id === right.id) {
                        return 1;
                    }
                    // otherwise compare the primary to the backup's primary
                    return enhancedComparator(
                        resolveLivePrimary(left.backingUpUnitQueueView),
                        right
                    );
                }
                if (isPrimary(left) && isBackup(right)) {
                    // if the primary is the primary for this backup, the primary wins
                    if (right.backingUpUnitQueueView?.id === left.id) {
                        return -1;
                    }
                    // otherwise compare the primary to the backup's primary
                    return enhancedComparator(
                        left,
                        resolveLivePrimary(right.backingUpUnitQueueView)
                    );
                }

                throw new Error(
                    'Pin to Primary sort unexpectedly fell through to impossible case.'
                );
            },
        };
        return sortingDirective;
    }

    private createPinToPrimaryGroupingDirective(): GroupingDirective<UnitQueueRowData> | undefined {
        const baseGroupingDirective = this.createCombinedGroupingDirective();

        if (!baseGroupingDirective) {
            return undefined;
        }

        const groupingDirective: GroupingDirective<UnitQueueRowData> = {
            getGroupValue(item: UnitQueueRowData): string {
                // If the item is a backup unit, it assumes the group value of
                // its corresponding primary unit
                if (item.backingUpUnitQueueView) {
                    const primaryUnit = item.backingUpUnitQueueView;
                    if (primaryUnit) {
                        return baseGroupingDirective.getGroupValue(primaryUnit);
                    } else {
                        throw new Error(
                            'could not find primary unit when attempting to retrieve group value for backup unit'
                        );
                    }
                }
                return baseGroupingDirective.getGroupValue(item);
            },
            comparator: baseGroupingDirective.comparator,
        };

        return groupingDirective;
    }

    @override
    public setSort(
        columnDefinitionName?: ColumnDefinitionEnumType,
        sortDirection?: WorkspaceQueue['sortDirection']
    ): void {
        // Hard code that every unit queue sort implicitly sorts emergency to the top.
        super.setSort(columnDefinitionName, sortDirection, true);
        if (this.workspacePanel.queue.pinBackupsToPrimaryUnits) {
            this.setPinToPrimarySortingAndGroupingDirectives();
        } else {
            this.listStore.setSortingDirective(this.createCombinedSortingDirective());
        }
    }

    @override
    public setGroupByQueueColumnDefinition(definition?: ColumnDefinitionEnumType): void {
        super.setGroupByQueueColumnDefinition(definition, true);
        if (this.workspacePanel.queue.pinBackupsToPrimaryUnits) {
            this.setPinToPrimarySortingAndGroupingDirectives();
        } else {
            this.listStore.setGroupingDirective(this.createCombinedGroupingDirective());
        }
    }

    @override
    public setGroupBySortDirection(direction: WorkspaceQueue['groupBySortDirection']): void {
        super.setGroupBySortDirection(direction, true);
        if (this.workspacePanel.queue.pinBackupsToPrimaryUnits) {
            this.setPinToPrimarySortingAndGroupingDirectives();
        } else {
            this.listStore.setGroupingDirective(this.createCombinedGroupingDirective());
        }
    }

    @action
    private setCombinedSortingAndGroupingDirectives(): void {
        this.setSort(
            this.workspacePanel.queue.sortByQueueColumnDefinition,
            this.workspacePanel.queue.sortDirection
        );
        // emptying the group column before setting the correct value allows us to
        // get the desired effect - otherwise setting the group column to it's current
        // value will unset it. This superfluous computation takes place in an action
        // so should not correspond to any rendering
        const columnToRetain = this.workspacePanel.queue.groupByQueueColumnDefinition;
        this.setGroupByQueueColumnDefinition();
        this.setGroupByQueueColumnDefinition(columnToRetain);
        this.setGroupBySortDirection(this.workspacePanel.queue.groupBySortDirection);
    }

    @action
    private setPinToPrimarySortingAndGroupingDirectives(): void {
        this.listStore.setGroupingDirective(this.createPinToPrimaryGroupingDirective());
        this.listStore.setSortingDirective(this.createPinToPrimarySortingDirective());
    }

    @action
    public togglePinToPrimary(): void {
        if (!this.workspacePanel.queue.pinBackupsToPrimaryUnits) {
            this.setPinToPrimarySortingAndGroupingDirectives();
        } else {
            this.setCombinedSortingAndGroupingDirectives();
        }
        this.workspacePanel.queue.pinBackupsToPrimaryUnits = !this.workspacePanel.queue
            .pinBackupsToPrimaryUnits;
    }
}
